import React, { useMemo } from 'react';
import { Alert, Box, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import { fitStraightLine, type DataPoint } from './physics/ohmsLaw';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);
export type GraphMode = 'vi' | 'iv' | 'rl';

interface GraphPlotProps {
  readings: Array<{ voltage: number; current: number; resistance: number; lengthMetres?: number }>;
  mode: GraphMode;
  onModeChange: (mode: GraphMode) => void;
}

const GraphPlot: React.FC<GraphPlotProps> = ({ readings, mode, onModeChange }) => {
  const plot = useMemo(() => {
    const points: DataPoint[] = readings.map((reading) => mode === 'vi'
      ? { x: reading.current, y: reading.voltage }
      : mode === 'iv'
        ? { x: reading.voltage, y: reading.current }
        : { x: reading.lengthMetres ?? readings.indexOf(reading) + 1, y: reading.resistance });
    let fit = null;
    try { if (points.length >= 2) fit = fitStraightLine(points); } catch { fit = null; }
    const sorted = [...points].sort((a, b) => a.x - b.x);
    const line = fit && sorted.length ? [
      { x: sorted[0].x, y: fit.slope * sorted[0].x + fit.intercept },
      { x: sorted[sorted.length - 1].x, y: fit.slope * sorted[sorted.length - 1].x + fit.intercept },
    ] : [];
    return { points, fit, line };
  }, [readings, mode]);

  const labels = mode === 'vi'
    ? { x: 'Current (A)', y: 'Voltage (V)', title: 'Voltage–current characteristic' }
    : mode === 'iv'
      ? { x: 'Voltage (V)', y: 'Current (A)', title: 'Current–voltage characteristic' }
      : { x: 'Wire length (m)', y: 'Resistance (Ω)', title: 'Resistance–length relationship' };

  const data = {
    datasets: [
      { label: 'Recorded readings', data: plot.points, parsing: false, showLine: false, pointRadius: 5, pointBackgroundColor: '#22c1c3', borderColor: '#22c1c3' },
      ...(plot.line.length ? [{ label: 'Best-fit line', data: plot.line, parsing: false, showLine: true, pointRadius: 0, borderColor: '#f59e0b', borderDash: [7, 5], borderWidth: 2 }] : []),
    ],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const }, tooltip: { enabled: true } },
    scales: {
      x: { type: 'linear' as const, title: { display: true, text: labels.x }, grid: { color: 'rgba(148,163,184,.15)' } },
      y: { title: { display: true, text: labels.y }, grid: { color: 'rgba(148,163,184,.15)' } },
    },
  };

  return (
    <Paper className="ohm-panel" elevation={0}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1} sx={{ mb: 1.5 }}>
        <Box><Typography variant="h6" fontWeight={800}>Live graph</Typography><Typography variant="body2" color="text.secondary">{labels.title}</Typography></Box>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, value: GraphMode | null) => value && onModeChange(value)} aria-label="Graph type">
          <ToggleButton value="vi">V–I</ToggleButton><ToggleButton value="iv">I–V</ToggleButton><ToggleButton value="rl">R–length</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ height: 260 }}><Line data={data as any} options={options as any} /></Box>
      {plot.fit && <Alert severity="info" sx={{ mt: 1.5 }}>
        Best fit: y = {plot.fit.slope.toFixed(4)}x {plot.fit.intercept >= 0 ? '+' : '−'} {Math.abs(plot.fit.intercept).toFixed(4)} · slope {plot.fit.slope.toFixed(4)} · R² {plot.fit.rSquared.toFixed(4)}
      </Alert>}
      {plot.points.length < 2 && <Typography variant="caption" color="text.secondary">Record at least two observations to calculate a best-fit line and R².</Typography>}
    </Paper>
  );
};

export default GraphPlot;
